import api from '@/lib/api'
import type { PaginatedResponse } from '@/types'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface BillingPlan {
  id: string
  /** null = platform plan; UUID = domain plan */
  domain_id?: string | null
  name: string
  billing_cycle: 'monthly' | 'quarterly' | 'biannual' | 'annual'
  /** When to generate the bill: advance = period start, arrear = period end */
  billing_mode: 'advance' | 'arrear' | null
  unit_type: 'vehicle' | 'employee' | 'student' | 'pet'
  price_per_unit: number
  currency: string
  /** Floor used when computing invoice amount: billable = MAX(actual_count, min_billable_units). 0 = pure usage-based. */
  min_billable_units: number
  is_active: boolean
  /** HSN/SAC code for GST line-item classification (set by platform admin) */
  hsn_sac_code?: string | null
  /** Minimum amount charged per cycle while subscription is paused (covers SIM/infra costs). 0 = no charge during pause. */
  pause_minimum_amount?: number | null
  created_at: string
  /** Count of non-cancelled domain subscriptions using this plan (super admin only) */
  subscription_count?: number | null
}

export interface PlanDomainRow {
  domain_id: string
  domain_name: string
  domain_hostname: string
  subscription_id: string
  status: string
  unit_count: number
  current_period_start: string
  current_period_end: string
  next_billing_at: string | null
}

export interface DomainSubscription {
  id: string
  domain_id: string
  plan_id: string
  plan: BillingPlan | null
  /** YAML schema: [active, suspended, cancelled] — domain subscriptions cannot be paused */
  status: 'active' | 'suspended' | 'cancelled'
  /** Snapshotted from plan at activation */
  billing_mode: 'advance' | 'arrear' | null
  unit_count: number
  /** Snapshotted from plan (e.g. 'vehicle', 'employee') — available at subscription level */
  unit_type: string | null
  current_period_start: string
  current_period_end: string
  next_billing_at: string | null
  razorpay_subscription_id: string | null
  created_at: string
  updated_at: string
  /** Pre-formatted by backend using domain date_format setting */
  current_period_start_display?: string | null
  current_period_end_display?: string | null
  next_billing_at_display?: string | null
  created_at_display?: string | null
}

export interface CustomerSubscription {
  id: string
  domain_id: string
  customer_user_id: string
  plan_id: string
  plan: BillingPlan | null
  status: 'active' | 'paused' | 'suspended' | 'cancelled'
  /** Snapshotted from plan at activation */
  billing_mode: 'advance' | 'arrear' | null
  unit_count: number
  /** Day-of-month (1–28) the subscription is anchored to; null = cycle from activation */
  billing_anchor_day: number | null
  current_period_start: string
  current_period_end: string
  next_billing_at: string | null
  /** If set, subscription auto-resumes on this date */
  paused_until: string | null
  razorpay_subscription_id: string | null
  created_at: string
  updated_at: string
}

export interface SubscriptionEntity {
  id: string
  subscription_id: string
  entity_type: 'vehicle' | 'employee' | 'student' | 'pet'
  entity_id: string
  /** Registration number (vehicles) or identifier — populated by backend JOIN */
  entity_name: string | null
  /** Extra fields e.g. { name, registration_number } for vehicles */
  entity_meta: Record<string, string> | null
  is_suspended: boolean
  suspended_at: string | null
  suspended_reason: string | null
  restored_at: string | null
  created_at: string
  updated_at: string
}

export interface Invoice {
  id: string
  domain_id: string
  invoice_number: string
  /** Timestamp when a proforma was converted to a tax_invoice */
  proforma_converted_at: string | null
  /**
   * bill        — demand note (BILL-XXXXXXXX temp number, purple) — editable/cancellable
   * tax_invoice — auto-generated on payment capture; gapless INV-YYYY-NNNNNN serial; teal
   * invoice     — same as tax_invoice but seller has no GST registration; blue
   * credit_note — issued to reverse a charge; increases account balance
   */
  invoice_type: 'bill' | 'tax_invoice' | 'invoice' | 'credit_note' | null
  /** Set on tax_invoice / invoice rows — references the payment that triggered generation */
  payment_id: string | null
  subscription_id: string | null
  subscription_type: 'platform' | 'domain' | null
  customer_user_id: string | null
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'void'
  amount: number
  subtotal_amount: number | null
  tax_amount: number | null
  currency: string
  due_date: string | null
  paid_at: string | null
  notes: string | null
  /** Free-text description (set on manually created invoices) */
  description: string | null
  /** Snapshot of seller billing profile at time of invoice generation */
  seller_snapshot: Record<string, unknown> | null
  /** Snapshot of buyer billing profile at time of invoice generation */
  buyer_snapshot: Record<string, unknown> | null
  /** Itemised tax lines — CGST+SGST (intra-state) or IGST (inter-state/unknown) */
  tax_breakdown: { type: string; rate: number; amount: number }[] | null
  /** Rich line items per subscription line */
  line_items: {
    description: string
    unit_type?: string
    plan_name?: string
    actual_count?: number
    billable_units?: number
    min_billable_units?: number
    price_per_unit?: number
    subtotal?: number
    amount: number
  }[] | null
  /** Dunning — populated when reminder emails are sent */
  reminder_1_sent_at: string | null
  reminder_2_sent_at: string | null
  /** Timestamp of the "suspension warning" dunning notification */
  suspension_notified_at: string | null
  created_at: string
  updated_at: string
}

export interface TaxConfig {
  default_currency: string
  tax_name: string | null
  tax_rate: number | null
  tax_number: string | null
  tax_number_label: string | null
  invoice_prefix: string | null
  fy_start_month: number | null
  invoice_include_fy: boolean
}

export interface Payment {
  id: string
  domain_id: string
  customer_user_id: string | null
  invoice_id: string | null
  payment_type: 'received' | 'wallet_topup' | 'invoice' | 'prorata_credit' | 'credit_note' | 'refund'
  amount: number
  currency: string
  gateway: string
  gateway_payment_id: string | null
  gateway_order_id: string | null
  reference_number: string | null
  notes: string | null
  status: 'pending' | 'captured' | 'failed' | 'refunded' | 'cancelled'
  paid_at: string | null
  created_at: string
  updated_at: string
  /** Pre-formatted by backend using the user/domain date_format setting */
  paid_at_display?: string | null
  created_at_display?: string | null
}

export interface WalletBalance {
  domain_id: string
  user_id: string | null
  balance: number
  currency: string
}

/** A single line in an account statement — payment (credit), bill (debit), or credit note (credit) */
export interface StatementEntry {
  type: 'credit' | 'debit'
  ref_type: 'payment' | 'bill' | 'credit_note'
  ref_id: string
  date: string
  description: string
  amount: number
  currency: string
  running_balance: number
}

export interface StatementResponse {
  opening_balance: number
  closing_balance: number
  currency: string
  entries: StatementEntry[]
}

/** @deprecated Use StatementEntry instead */
export interface WalletEntry {
  id: string
  domain_id: string
  user_id: string | null
  entry_type: 'credit' | 'debit'
  amount: number
  currency: string
  reference_type: string | null
  balance_after: number
  description: string | null
  created_at: string
}

export interface PaymentGatewayConfig {
  id: string
  domain_id: string | null
  gateway: string
  label: string | null
  is_active: boolean
  config: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface BillingProfile {
  id: string
  domain_id: string | null
  user_id: string | null
  legal_name: string
  business_type: 'individual' | 'business'
  tax_id_type: string | null
  tax_id: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  country: string | null
  postal_code: string | null
  invoice_email: string | null
  currency: string
  extra: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

// ── Platform Plans ─────────────────────────────────────────────────────────────

export const listBillingPlans = () =>
  api.get<{ success: true; data: BillingPlan[] }>('/billing/plans').then((r) => r.data.data ?? [])

export const createBillingPlan = (d: Omit<BillingPlan, 'id' | 'created_at'>) =>
  api.post<{ success: true; data: BillingPlan }>('/billing/plans', d).then((r) => r.data.data)

export const updateBillingPlan = (id: string, d: { name?: string; price_per_unit?: number; currency?: string; is_active?: boolean; hsn_sac_code?: string | null; pause_minimum_amount?: number | null }) =>
  api.put<{ success: true; data: BillingPlan }>(`/billing/plans/${id}`, d).then((r) => r.data.data)

export const deleteBillingPlan = (id: string) =>
  api.delete(`/billing/plans/${id}`).then((r) => r.data)

/** List domains subscribed to a platform plan. Super admin only. */
export const getPlanDomains = (planId: string, status?: string) =>
  api
    .get<{ success: true; data: PlanDomainRow[]; meta: { total: number } }>(
      `/billing/plans/${planId}/domains`,
      { params: status ? { status } : undefined },
    )
    .then((r) => r.data)

// ── Domain Plans ───────────────────────────────────────────────────────────────

export const listDomainPlans = () =>
  api.get<{ success: true; data: BillingPlan[] }>('/billing/domain-plans').then((r) => r.data.data ?? [])

export const createDomainPlan = (d: Omit<BillingPlan, 'id' | 'created_at'>) =>
  api.post<{ success: true; data: BillingPlan }>('/billing/domain-plans', d).then((r) => r.data.data)

export const updateDomainPlan = (id: string, d: { name?: string; price_per_unit?: number; currency?: string; is_active?: boolean; hsn_sac_code?: string | null }) =>
  api.put<{ success: true; data: BillingPlan }>(`/billing/domain-plans/${id}`, d).then((r) => r.data.data)

export const deleteDomainPlan = (id: string) =>
  api.delete(`/billing/domain-plans/${id}`).then((r) => r.data)

export interface PlanSubscriber {
  user_id: string
  name: string
  email: string | null
  phone: string | null
  subscription_id: string
  status: string
  subscribed_at: string
  subscribed_at_display?: string | null
}

/** List all customers subscribed to a given domain plan */
export const listPlanSubscribers = (planId: string): Promise<PlanSubscriber[]> =>
  api
    .get<{ success: true; data: PlanSubscriber[] }>(`/billing/domain-plans/${planId}/subscribers`)
    .then((r) => r.data.data ?? [])

// ── Domain Subscription ────────────────────────────────────────────────────────

export const getSubscription = () =>
  api.get<{ success: true; data: DomainSubscription | DomainSubscription[] }>('/billing/subscription').then((r) => {
    const d = r.data.data
    return Array.isArray(d) ? (d[0] ?? null) : d
  })

export const activateSubscription = (body: {
  plan_id: string
  unit_count?: number
  payment_method?: 'wallet' | 'gateway'
  gateway_order_id?: string
}) =>
  api.post<{ success: true; data: DomainSubscription }>('/billing/subscription/activate', body).then((r) => r.data.data)

export const cancelSubscription = (reason?: string) =>
  api.post('/billing/subscription/cancel', { reason }).then((r) => r.data)

export const changeSubscriptionPlan = (plan_id: string, effective?: 'immediate' | 'next_cycle') =>
  api.put<{ success: true; data: DomainSubscription }>('/billing/subscription/plan', { plan_id, effective }).then((r) => r.data.data)

// NOTE: Domain subscriptions do not support pause/resume per the API spec.
// Only customer subscriptions (/billing/customers/{id}/subscription/pause) support pause.

// ── Customer Subscriptions ─────────────────────────────────────────────────────

export const getCustomerSubscription = (userId: string) =>
  api
    .get<{ success: true; data: CustomerSubscription[] }>(`/billing/customers/${userId}/subscription`)
    .then((r) => r.data.data ?? [])

export const createCustomerSubscription = (
  userId: string,
  body: { plan_id: string; billing_anchor_day?: number | null },
) =>
  api
    .post<{ success: true; data: CustomerSubscription }>(
      `/billing/customers/${userId}/subscription`,
      body,
    )
    .then((r) => r.data.data)

export const cancelCustomerSubscriptionById = (userId: string, subId: string) =>
  api.delete(`/billing/customers/${userId}/subscription/${subId}`).then((r) => r.data)

export const changeCustomerSubscriptionPlan = (userId: string, plan_id: string, effective?: 'immediate' | 'next_cycle') =>
  api.put<{ success: true; data: CustomerSubscription }>(`/billing/customers/${userId}/subscription/plan`, { plan_id, effective }).then((r) => r.data.data)

export const pauseCustomerSubscription = (userId: string, until?: string) =>
  api.post<{ success: true; data: CustomerSubscription }>(`/billing/customers/${userId}/subscription/pause`, until ? { until } : undefined).then((r) => r.data.data)

export const resumeCustomerSubscription = (userId: string) =>
  api.post<{ success: true; data: CustomerSubscription }>(`/billing/customers/${userId}/subscription/resume`).then((r) => r.data.data)

// ── Subscription Entities ──────────────────────────────────────────────────────

export const listSubscriptionEntities = (userId: string, subId: string) =>
  api
    .get<{ success: true; data: SubscriptionEntity[] | null }>(
      `/billing/customers/${userId}/subscription/${subId}/entities`,
    )
    .then((r) => r.data.data ?? [])

export const addSubscriptionEntity = (
  userId: string,
  subId: string,
  body: { entity_type: SubscriptionEntity['entity_type']; entity_id: string },
) =>
  api
    .post<{ success: true; data: SubscriptionEntity }>(
      `/billing/customers/${userId}/subscription/${subId}/entities`,
      body,
    )
    .then((r) => r.data.data)

export const removeSubscriptionEntity = (userId: string, subId: string, entityId: string) =>
  api
    .delete(`/billing/customers/${userId}/subscription/${subId}/entities/${entityId}`)
    .then((r) => r.data)

export const suspendSubscriptionEntity = (userId: string, subId: string, entityId: string, reason?: string) =>
  api
    .post(
      `/billing/customers/${userId}/subscription/${subId}/entities/${entityId}/suspend`,
      { reason },
    )
    .then((r) => r.data)

export const restoreSubscriptionEntity = (userId: string, subId: string, entityId: string) =>
  api
    .post(`/billing/customers/${userId}/subscription/${subId}/entities/${entityId}/restore`)
    .then((r) => r.data)

/** Admin: all billed entities for a customer across their subscription */
export const listCustomerEntities = (userId: string) =>
  api
    .get<{ success: true; data: SubscriptionEntity[] | null }>(`/billing/customers/${userId}/entities`)
    .then((r) => r.data.data ?? [])

/** Owner self-service: own billed entities */
export const listMyEntities = () =>
  api
    .get<{ success: true; data: SubscriptionEntity[] | null }>('/billing/my/entities')
    .then((r) => r.data.data ?? [])

// ── Bills (formerly /billing/invoices — those are now deprecated aliases) ──────

/** List bills for this domain. Supports status filter + pagination. */
export const listInvoices = (p?: {
  status?: Invoice['status']
  page?: number
  limit?: number
}) =>
  api.get<PaginatedResponse<Invoice>>('/billing/bills', { params: p }).then((r) => r.data)

export const getInvoice = (id: string) =>
  api
    .get<{ success: true; data: Invoice }>(`/billing/bills/${id}`)
    .then((r) => r.data.data)

/** Update mutable fields on a bill (invoice_type = 'bill' only). Tax invoices and credit notes are read-only. */
export const updateInvoice = (id: string, body: { due_date?: string; notes?: string | null }) =>
  api.put<{ success: true; data: Invoice }>(`/billing/bills/${id}`, body).then((r) => r.data.data)

export const cancelInvoice = (id: string, reason?: string) =>
  api.post(`/billing/bills/${id}/cancel`, { reason }).then((r) => r.data)

/** Download bill PDF via authenticated request (avoids 401 on direct URL open) */
export const downloadInvoicePdf = async (id: string, invoiceNumber?: string): Promise<void> => {
  const res = await api.get(`/billing/bills/${id}/pdf`, { responseType: 'blob' })
  const blob = new Blob([res.data as BlobPart], { type: 'application/pdf' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `${invoiceNumber ?? id}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/** Record a manual (offline) payment against a bill */
export const recordManualPayment = (id: string, body: { amount: number; currency: string; payment_method: string; reference_number?: string; notes?: string; paid_at?: string }) =>
  api.post<{ success: true; data: Invoice }>(`/billing/bills/${id}/payments/manual`, body).then((r) => r.data.data)

/** Issue a credit note against a bill */
export const issueCreditNote = (id: string, body: { amount: number; reason?: string }) =>
  api.post<{ success: true; data: Invoice }>(`/billing/bills/${id}/credit-note`, body).then((r) => r.data.data)

/** Update bill status (send, mark overdue, mark draft, etc.) */
export const updateInvoiceStatus = (id: string, status: Invoice['status']) =>
  api.patch<{ success: true; data: Invoice }>(`/billing/bills/${id}/status`, { status }).then((r) => r.data.data)

/**
 * Create a manual one-off bill (setup fees, hardware, custom work).
 * line_items and due_date are required per spec.
 */
export const createManualInvoice = (body: {
  customer_user_id?: string | null
  /** Super-admin only: create bill against a specific domain */
  domain_id?: string | null
  /** Line items are required for manual bills */
  line_items: { description: string; amount: number }[]
  due_date: string
  notes?: string | null
}) =>
  api.post<{ success: true; data: Invoice }>('/billing/bills/manual', body).then((r) => r.data.data)

// ── Payments ───────────────────────────────────────────────────────────────────

/** GET /billing/payments — super admin can scope by domain_id */
export const listPayments = (p?: { page?: number; limit?: number; domain_id?: string }) =>
  api.get<PaginatedResponse<Payment>>('/billing/payments', { params: p }).then((r) => r.data)

export const getPayment = (id: string) =>
  api.get<{ success: true; data: Payment }>(`/billing/payments/${id}`).then(r => r.data.data)

/** POST /billing/payments — record a standalone payment */
export const createPayment = (body: {
  customer_user_id?: string | null
  /** Super-admin only: record payment against a specific domain */
  domain_id?: string | null
  amount: number
  currency?: string
  gateway?: string
  paid_at?: string
  reference_number?: string
  notes?: string
}) =>
  api.post<{ success: true; data: Payment }>('/billing/payments', body).then(r => r.data.data)

/** POST /billing/payments/{id}/cancel — cancel a captured payment */
export const cancelPayment = (id: string, reason?: string) =>
  api.post<{ success: true; data: Payment }>(`/billing/payments/${id}/cancel`, reason ? { reason } : undefined).then(r => r.data.data)

export const getBillingBalance = () =>
  api.get<{ success: true; data: WalletBalance }>('/billing/balance').then(r => r.data.data)

export const topUpBalance = (amount: number, currency: string) =>
  api.post<{ success: true; data: WalletBalance }>('/billing/balance/topup', { amount, currency }).then(r => r.data.data)

// ── Account Balance & Statement ────────────────────────────────────────────────

/** Get domain-level or customer-level account balance. Pass user_id for customer balance. */
export const getStatement = (p?: {
  user_id?: string
  /** Super-admin only: specify which domain's statement to fetch */
  domain_id?: string
  from?: string
  to?: string
  page?: number
  limit?: number
}) =>
  api
    .get<{ success: true; data: StatementResponse; meta: { total: number; page: number; limit: number } }>(
      '/billing/statement',
      { params: p },
    )
    .then((r) => r.data)

/** @deprecated Use getBillingBalance instead */
export const getWalletBalance = () =>
  api.get<{ success: true; data: WalletBalance }>('/billing/wallet').then((r) => r.data.data)

/** @deprecated Use getStatement instead */
export const listWalletStatement = (p?: { page?: number; per_page?: number }) =>
  api.get<PaginatedResponse<WalletEntry>>('/billing/statement', { params: p }).then((r) => r.data)

/** @deprecated Use topUpBalance instead */
export const topUpWallet = (amount: number, currency: string, gatewayOrderId?: string) =>
  api
    .post<{ success: true; data: WalletBalance }>('/billing/wallet/topup', {
      amount,
      currency,
      gateway_order_id: gatewayOrderId,
    })
    .then((r) => r.data.data)

// ── Platform Gateways (super-admin) ───────────────────────────────────────────

export const listPlatformGateways = () =>
  api
    .get<{ success: true; data: PaymentGatewayConfig[] }>('/billing/platform/gateways')
    .then((r) => r.data.data ?? [])

export const createPlatformGateway = (body: {
  gateway: string
  label?: string
  config: Record<string, unknown>
  is_active?: boolean
}) =>
  api
    .post<{ success: true; data: PaymentGatewayConfig }>('/billing/platform/gateways', body)
    .then((r) => r.data.data)

export const updatePlatformGateway = (
  id: string,
  body: { gateway: string; label?: string; config?: Record<string, unknown> },
) =>
  api
    .put<{ success: true; data: PaymentGatewayConfig }>(`/billing/platform/gateways/${id}`, body)
    .then((r) => r.data.data)

export const deletePlatformGateway = (id: string) =>
  api.delete(`/billing/platform/gateways/${id}`).then((r) => r.data)

export const activatePlatformGateway = (id: string) =>
  api.post(`/billing/platform/gateways/${id}/activate`).then((r) => r.data)

// ── Domain Gateways ────────────────────────────────────────────────────────────

export const listDomainGateways = () =>
  api
    .get<{ success: true; data: PaymentGatewayConfig[] }>('/billing/gateways')
    .then((r) => r.data.data ?? [])

export const createDomainGateway = (body: {
  gateway: string
  label?: string
  config: Record<string, unknown>
  is_active?: boolean
}) =>
  api
    .post<{ success: true; data: PaymentGatewayConfig }>('/billing/gateways', body)
    .then((r) => r.data.data)

export const updateDomainGateway = (id: string, body: { gateway: string; label?: string; config?: Record<string, unknown> }) =>
  api
    .put<{ success: true; data: PaymentGatewayConfig }>(`/billing/gateways/${id}`, body)
    .then((r) => r.data.data)

export const deleteDomainGateway = (id: string) =>
  api.delete(`/billing/gateways/${id}`).then((r) => r.data)

export const activateDomainGateway = (id: string) =>
  api.post(`/billing/gateways/${id}/activate`).then((r) => r.data)

// ── Billing Profiles ───────────────────────────────────────────────────────────

export const getPlatformProfile = () =>
  api
    .get<{ success: true; data: BillingProfile }>('/billing/platform/profile')
    .then((r) => r.data.data)

export const updatePlatformProfile = (body: Partial<BillingProfile>) =>
  api
    .put<{ success: true; data: BillingProfile }>('/billing/platform/profile', body)
    .then((r) => r.data.data)

export const getDomainProfile = () =>
  api
    .get<{ success: true; data: BillingProfile }>('/billing/profile')
    .then((r) => r.data.data)

export const updateDomainProfile = (body: Partial<BillingProfile>) =>
  api
    .put<{ success: true; data: BillingProfile }>('/billing/profile', body)
    .then((r) => r.data.data)

export const getCustomerProfile = (userId: string) =>
  api
    .get<{ success: true; data: BillingProfile }>(`/billing/customers/${userId}/profile`)
    .then((r) => r.data.data)

export const updateCustomerProfile = (userId: string, body: Partial<BillingProfile>) =>
  api
    .put<{ success: true; data: BillingProfile }>(`/billing/customers/${userId}/profile`, body)
    .then(r => r.data.data)

// ── Super admin: per-domain billing profile ────────────────────────────────────

export const getDomainBillingProfile = (domainId: string) =>
  api
    .get<{ success: true; data: BillingProfile }>(`/domains/${domainId}/billing/profile`)
    .then(r => r.data.data)

export const updateDomainBillingProfile = (domainId: string, body: Partial<BillingProfile>) =>
  api
    .put<{ success: true; data: BillingProfile }>(`/domains/${domainId}/billing/profile`, body)
    .then(r => r.data.data)

// ── Super admin: per-domain subscription management ───────────────────────────

export const getDomainSubscription = (domainId: string) =>
  api
    .get<{ success: true; data: DomainSubscription | DomainSubscription[] }>(`/domains/${domainId}/subscription`)
    .then(r => {
      const d = r.data.data
      return Array.isArray(d) ? d : [d]
    })

export const cancelDomainSubscription = (domainId: string) =>
  api.post(`/domains/${domainId}/subscription/cancel`).then(r => r.data)

export const activateDomainSubscription = (domainId: string, body: { plan_id: string; unit_count: number }) =>
  api
    .post<{ success: true; data: DomainSubscription }>(`/domains/${domainId}/subscription/activate`, body)
    .then(r => r.data.data)

export const changeDomainSubscriptionPlan = (domainId: string, body: { plan_id: string; unit_count?: number }) =>
  api
    .put<{ success: true; data: DomainSubscription }>(`/domains/${domainId}/subscription/plan`, body)
    .then(r => r.data.data)

// ── PDF Templates ──────────────────────────────────────────────────────────────

export interface PdfTemplate {
  id?: string
  domain_id?: string
  /** API field name — the HTML content of the template */
  body_html: string | null
  template_type?: string
  name?: string
  updated_at: string | null
}

export const listPdfTemplates = () =>
  api.get<{ success: true; data: PdfTemplate[] }>('/billing/pdf-templates').then(r => r.data.data ?? [])

export const getPdfTemplate = (type: string) =>
  api.get<{ success: true; data: PdfTemplate }>(`/billing/pdf-templates/${type}`).then(r => r.data.data)

export const updatePdfTemplate = (type: string, body: { body_html: string }) =>
  api.put<{ success: true; data: PdfTemplate }>(`/billing/pdf-templates/${type}`, body).then(r => r.data.data)

export const deletePdfTemplate = (type: string) =>
  api.delete(`/billing/pdf-templates/${type}`).then(r => r.data)

// ── Signing Certificate ────────────────────────────────────────────────────────

export interface SigningCert {
  id: string
  domain_id: string
  subject_cn: string
  org_name: string
  serial: string
  expires_at: string
  expires_at_display: string | null
  is_active: boolean
  uploaded_by: string | null
  created_at: string
  created_at_display: string | null
}

export const getSigningCert = () =>
  api.get<{ success: true; data: SigningCert | null }>('/billing/signing-cert').then(r => r.data.data ?? null)

/** Upload a PFX/PKCS#12 certificate for PDF signing (base64-encoded PFX + optional password) */
export const uploadSigningCert = (body: { pfx_base64: string; password?: string }) =>
  api.post<{ success: true; data: SigningCert }>('/billing/signing-cert', body).then(r => r.data.data)

/** Revoke / delete the active signing certificate */
export const deleteSigningCert = () =>
  api.delete('/billing/signing-cert').then(r => r.data)

// ── Tax Config ─────────────────────────────────────────────────────────────────

export const getTaxConfig = () =>
  api.get<{ success: true; data: TaxConfig }>('/billing/tax-config').then(r => r.data.data)

export const updateTaxConfig = (body: Partial<TaxConfig>) =>
  api.put<{ success: true; data: TaxConfig }>('/billing/tax-config', body).then(r => r.data.data)

/** Super-admin: platform-level tax config (applied to platform invoices sent to domain admins) */
export const getPlatformTaxConfig = () =>
  api.get<{ success: true; data: TaxConfig }>('/billing/platform/tax-config').then(r => r.data.data)

export const updatePlatformTaxConfig = (body: Partial<TaxConfig>) =>
  api.put<{ success: true; data: TaxConfig }>('/billing/platform/tax-config', body).then(r => r.data.data)

export interface GSTLookupResult {
  gst_number: string
  format_valid: boolean
  state_code: string
  trade_name: string
  legal_name: string
  status: string
  verified: boolean
  cached_at: string | null
}

export const lookupGst = (gstNumber: string) =>
  api.get<{ success: true; data: GSTLookupResult }>(`/billing/gst/${gstNumber}`).then(r => r.data.data)

/** Force-refresh GSTIN data from external GST API (bypasses 24-hour cache) */
export const refreshGstLookup = (gstNumber: string) =>
  api.post<{ success: true; data: GSTLookupResult }>(`/billing/gst/${gstNumber}/refresh`).then(r => r.data.data)

// ── GST Exports ────────────────────────────────────────────────────────────────

/** Download GSTR-1 JSON for the given period (format: MMYYYY, e.g. "042026") */
export const downloadGstr1Export = (period: string) =>
  api.get('/billing/exports/gstr1', { params: { period }, responseType: 'blob' }).then(r => r.data as Blob)

/** Download GSTN-compatible Excel (.xlsx) workbook for a period (format: MMYYYY, e.g. "042026") */
export const downloadGstnExcelExport = (period: string) =>
  api.get('/billing/exports/gstn-excel', { params: { period }, responseType: 'blob' }).then(r => r.data as Blob)

// ── States ─────────────────────────────────────────────────────────────────────

export interface BillingState {
  code: string
  name: string
  is_union_territory: boolean
  country: string
}

/** List canonical billing states/provinces for a country. Currently only 'IN' returns data. */
export const getBillingStates = (country = 'IN') =>
  api.get<{ success: true; data: BillingState[] }>('/billing/states', { params: { country } }).then(r => r.data.data ?? [])

export interface BillingCountry {
  code: string
  name: string
}

/** List all supported country codes (ISO 3166-1 alpha-2). */
export const getBillingCountries = () =>
  api.get<{ success: true; data: BillingCountry[] }>('/billing/countries').then(r => r.data.data ?? [])

// ── GSTIN Verification ─────────────────────────────────────────────────────────

export interface GstinVerifyResult {
  gstin: string
  legal_name: string
  trade_name: string
  status: string
  state_code: string
  business_type: string
  registered_at: string
  address: string
}

/** Verify a GSTIN and fetch registered business details. Throws on 422 (not found) or 503 (provider down). */
export const verifyGstin = (gstin: string) =>
  api.post<{ success: true; data: GstinVerifyResult }>('/billing/verify-gstin', { gstin }).then(r => r.data.data)

// ── Currencies ─────────────────────────────────────────────────────────────────

export const getCurrencies = () =>
  api.get<{ success: true; data: string[] }>('/billing/currencies').then(r => r.data.data ?? [])

export const getPlatformCurrencies = () =>
  api.get<{ success: true; data: string[] }>('/billing/platform/currencies').then(r => r.data.data ?? [])

// ── Billing Summary ────────────────────────────────────────────────────────────

export interface BillingSummary {
  active_subscriptions: number
  overdue_invoices: number
  overdue_amount: number
  wallet_balance: number
  currency: string
  next_renewal_at: string | null
}

export interface PlatformBillingSummary {
  active_domain_subscriptions: number
  active_customer_subscriptions: number
  overdue_invoices: number
  overdue_amount: number
}

export const getBillingSummary = () =>
  api.get<{ success: true; data: BillingSummary }>('/billing/summary').then(r => r.data.data)

export const getPlatformBillingSummary = () =>
  api.get<{ success: true; data: PlatformBillingSummary }>('/billing/platform/summary').then(r => r.data.data)

export const getBillingDashboard = () =>
  api.get<{ success: true; data: Record<string, unknown> }>('/billing/dashboard').then(r => r.data.data)

export const getPlatformBillingDashboard = () =>
  api.get<{ success: true; data: Record<string, unknown> }>('/billing/platform/dashboard').then(r => r.data.data)

// ── Renewals & Overdue ─────────────────────────────────────────────────────────

export interface UpcomingRenewal {
  customer_user_id: string
  customer_name: string
  plan_name: string
  renews_at: string
  amount: number
  currency: string
}

export interface OverdueInvoice {
  customer_user_id: string
  customer_name: string
  invoice_id: string
  invoice_number: string
  due_date: string
  amount: number
  currency: string
  days_overdue: number
}

export const getUpcomingRenewals = (days = 30) =>
  api.get<{ success: true; data: UpcomingRenewal[] }>('/billing/renewals', { params: { days } }).then(r => r.data.data ?? [])

export const getOverdueInvoices = () =>
  api.get<{ success: true; data: OverdueInvoice[] }>('/billing/overdue').then(r => r.data.data ?? [])

// ── Subscription History ───────────────────────────────────────────────────────

export interface SubscriptionEvent {
  id: string
  subscription_id: string
  subscription_type: 'customer' | 'domain'
  event_type: 'activated' | 'plan_changed' | 'paused' | 'resumed' | 'cancelled' | 'reactivated' | 'invoice_generated' | 'payment_received' | 'suspended' | 'restored'
  description: string
  performed_by: string | null
  meta: Record<string, unknown> | null
  created_at: string
  created_at_display: string | null
}

export const getSubscriptionHistory = (userId: string) =>
  api.get<{ success: true; data: SubscriptionEvent[] }>(`/billing/customers/${userId}/subscription/history`).then(r => r.data.data ?? [])

// ── Owner Self-Service (my/*) ──────────────────────────────────────────────────

export const getMySubscription = () =>
  api
    .get<{ success: true; data: CustomerSubscription[] }>('/billing/my/subscription')
    .then(r => r.data.data ?? [])

export const getMyInvoices = (p?: { page?: number; limit?: number; status?: string }) =>
  api.get<PaginatedResponse<Invoice>>('/billing/my/bills', { params: p }).then(r => r.data)

export const getMyBalance = () =>
  api.get<{ success: true; data: WalletBalance }>('/billing/my/balance').then(r => r.data.data)

/** @deprecated Use getMyBalance instead */
export const getMyWallet = () =>
  api.get<{ success: true; data: WalletBalance }>('/billing/my/wallet').then(r => r.data.data)

export const getMyStatement = (p?: { from?: string; to?: string; page?: number; limit?: number }) =>
  api
    .get<{ success: true; data: StatementResponse; meta: { total: number; page: number; limit: number } }>(
      '/billing/my/statement',
      { params: p },
    )
    .then(r => r.data)

/** @deprecated Use getMyStatement instead */
export const getMyWalletStatement = (p?: { page?: number; limit?: number }) =>
  api.get<PaginatedResponse<WalletEntry>>('/billing/my/statement', { params: p }).then(r => r.data)

export const getMyPayments = (p?: { page?: number; limit?: number }) =>
  api.get<PaginatedResponse<Payment>>('/billing/my/payments', { params: p }).then(r => r.data)

// ── Dunning Rules ──────────────────────────────────────────────────────────────

export interface DunningRule {
  id: string
  days: number
  direction: 'before_due' | 'after_due'
  channel: 'email' | 'whatsapp' | 'sms' | 'push'
  is_active: boolean
  created_at: string
  updated_at: string
}

export const getDunningRules = () =>
  api.get<{ success: true; data: DunningRule[] }>('/billing/dunning-rules').then(r => r.data.data ?? [])

export const createDunningRule = (body: {
  direction: 'before' | 'after'
  days: number
  channel: DunningRule['channel']
  is_active?: boolean
}) => {
  const { direction, days, ...rest } = body
  return api
    .post<{ success: true; data: DunningRule }>('/billing/dunning-rules', {
      direction: direction === 'before' ? 'before_due' : 'after_due',
      days,
      ...rest,
    })
    .then(r => r.data.data)
}

export const deleteDunningRule = (id: string) =>
  api.delete(`/billing/dunning-rules/${id}`).then(r => r.data)

// ── Plan Comparison ────────────────────────────────────────────────────────────

export interface ComparePlan extends BillingPlan {
  is_recommended: boolean
  annual_equivalent: number
  savings_vs_monthly: number | null
}

export const comparePlans = () =>
  api.get<{ success: true; data: ComparePlan[] }>('/billing/plans/compare').then(r => r.data.data ?? [])
